// src/components/issues/HistoriqueChamp.tsx — SCE Issues
// Historique d'une valeur corrigée (23/10) : l'icône à côté de la valeur, et le
// bloc gris qu'elle ouvre dessous. Commun aux commentaires et aux champs de la
// fiche (titre, description, phase, N° de plan, schéma électrique).

import { History } from 'lucide-react'
import { useMemo, useState, type CSSProperties } from 'react'
import { libelleCorrection, type VersionCommentaire } from '@/utils/editionCommentaire'
import { comparerTextes, replierEgal, type Morceau } from '@/utils/differenceTexte'

const DATE_HEURE = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Paris',
})

function dateHeure(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : DATE_HEURE.format(d).replace(',', '')
}

/**
 * L'icône Historique, si la valeur a changé (au moins deux textes). L'heure de
 * la dernière correction en infobulle ; au téléphone, dans le bloc qu'elle ouvre.
 */
export function BoutonHistorique({
  textes,
  libelle,
  ouvert,
  onBasculer,
}: {
  /** Le plus récent d'abord (versionsDuCommentaire). */
  textes: readonly VersionCommentaire[]
  /** « Titre », « Phase »… pour le lecteur d'écran et l'infobulle. */
  libelle: string
  ouvert: boolean
  onBasculer: () => void
}): JSX.Element | null {
  const actuel = textes[0]
  const origine = textes[textes.length - 1]
  if (!actuel || !origine || textes.length < 2) return null
  const titre = `${libelle} ${libelleCorrection(origine.date, actuel.date)} · voir l’historique`
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        onBasculer()
      }}
      aria-expanded={ouvert}
      aria-label={titre}
      title={titre}
      className="inline-flex items-center justify-center commentaire-modifie"
      data-ouvert={ouvert || undefined}
      style={{ verticalAlign: 'middle' }}
    >
      <History size={14} strokeWidth={2} />
    </button>
  )
}

/**
 * Les textes successifs, le plus récent d'abord. Chacun montre ce qui a changé
 * depuis le précédent (29/10, utils/differenceTexte) : mots retirés barrés, mots
 * ajoutés surlignés — avant, toute l'ancienne version était barrée.
 */
export function BlocHistorique({ textes, style }: { textes: readonly VersionCommentaire[]; style?: CSSProperties }): JSX.Element {
  return (
    <ol
      aria-label="Versions successives"
      className="flex flex-col"
      style={{
        listStyle: 'none',
        gap: 10,
        margin: '8px 0 0',
        padding: '10px 12px',
        borderRadius: 8,
        backgroundColor: 'var(--color-gray-5)',
        fontSize: 'var(--font-size-info)',
        lineHeight: '18px',
        fontWeight: 400,
        ...style,
      }}
    >
      {textes.map((v, i) => (
        <VersionHistorique key={v.date + i} version={v} precedente={textes[i + 1]} actuelle={i === 0} />
      ))}
    </ol>
  )
}

function VersionHistorique({
  version,
  precedente,
  actuelle,
}: {
  version: VersionCommentaire
  /** La version d'avant ; absente pour le texte d'origine. */
  precedente: VersionCommentaire | undefined
  actuelle: boolean
}): JSX.Element {
  const [entier, setEntier] = useState(false)
  // Rien à comparer : texte d'origine, texte effacé, ou écrit dans un champ vide.
  const difference = useMemo(
    () => (precedente?.contenu && version.contenu ? comparerTextes(precedente.contenu, version.contenu) : null),
    [precedente?.contenu, version.contenu],
  )
  const resume = !precedente ? null : !version.contenu ? 'Effacé' : !precedente.contenu ? 'Écrit' : difference?.resume
  return (
    <li className="flex flex-col" style={{ gap: 2 }}>
      <span style={{ color: 'var(--color-text-secondary)' }}>
        {actuelle ? 'Actuel' : version.origine ? 'D’origine' : 'Version précédente'} · {dateHeure(version.date)}
        {version.par && ` · par ${version.par}`}
        {resume && (
          <span style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}> · {resume}</span>
        )}
      </span>
      <span
        style={{
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          color: version.contenu ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
          fontStyle: version.contenu ? 'normal' : 'italic',
        }}
      >
        {!version.contenu
          ? '(vide)'
          : !difference || difference.reecrit
            ? version.contenu
            : difference.morceaux.map((m, k) => (
                <MorceauTexte
                  key={k}
                  morceau={m}
                  position={k === 0 ? 'debut' : k === difference.morceaux.length - 1 ? 'fin' : 'milieu'}
                  replier={!entier && difference.morceaux.length > 1}
                  onDeplier={() => setEntier(true)}
                />
              ))}
      </span>
    </li>
  )
}

const STYLE_AJOUT: CSSProperties = {
  textDecoration: 'none',
  backgroundColor: 'var(--color-yellow)',
  color: 'var(--color-black)',
  borderRadius: 2,
  padding: '0 1px',
  boxDecorationBreak: 'clone',
  WebkitBoxDecorationBreak: 'clone',
}

const STYLE_RETRAIT: CSSProperties = {
  color: 'var(--color-gray-70)',
  textDecoration: 'line-through',
  textDecorationColor: 'var(--color-gray-70)',
}

function MorceauTexte({
  morceau,
  position,
  replier,
  onDeplier,
}: {
  morceau: Morceau
  position: 'debut' | 'milieu' | 'fin'
  replier: boolean
  onDeplier: () => void
}): JSX.Element {
  if (morceau.type === 'ajout') return <ins style={STYLE_AJOUT}>{morceau.texte}</ins>
  if (morceau.type === 'retrait') return <del style={STYLE_RETRAIT}>{morceau.texte}</del>
  const repli = replier ? replierEgal(morceau.texte, position) : null
  if (!repli) return <>{morceau.texte}</>
  return (
    <>
      {repli.avant}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onDeplier()
        }}
        aria-label="Afficher tout le texte"
        title="Afficher tout le texte"
        className="historique-replie"
      >
        …
      </button>
      {repli.apres}
    </>
  )
}
